import _ from 'lodash';

import { addSkuNamePrefix } from '../../../utils';

import { Pack, PriceLevel } from '../../../../types/packTypes';

import { emptyPriceLevel } from '../../../emptyPack';

export const DiscountTypes = {
    percentage: 'percentage',
    flat: 'flat-value',
} as const;

export type DiscountType = (typeof DiscountTypes)[keyof typeof DiscountTypes];

export type PackWorthList = Pick<Pack, 'worth'>[];

export function createDiscountedPriceLevel(
    productId: string,
    price: number,
): PriceLevel {
    const newPriceLevel = _.cloneDeep(emptyPriceLevel) as unknown as PriceLevel;
    // Make so the number is always .99. So 4.99 instead of 5.00
    newPriceLevel.price = Math.round(price) - 0.01;
    newPriceLevel.productId = `${productId}.p${newPriceLevel.price.toFixed(2)}`;
    for (const platform of newPriceLevel.platformData) {
        platform.sku = newPriceLevel.productId;
    }
    return newPriceLevel;
}

export function createStrikethroughPriceLevel(
    productId: string,
    totalWorth: number,
): PriceLevel {
    const firstPriceLevel = createDiscountedPriceLevel(productId, totalWorth);
    firstPriceLevel.min = 1000;
    firstPriceLevel.max = 1000;
    return firstPriceLevel;
}

function createFirstDiscountedPriceLevel(
    productId: string,
    totalWorth: number,
    discountedTotal: number,
): PriceLevel {
    const firstPriceLevel = createDiscountedPriceLevel(
        productId,
        discountedTotal,
    );
    firstPriceLevel.min = totalWorth;
    firstPriceLevel.max = totalWorth;
    return firstPriceLevel;
}

type GenerateSKUsParameters = {
    name: string;
    totalWorth: number;
    childPacks: PackWorthList;
    discount: number;
    discountType: DiscountType;
    discountedTotal: number;
};

export function generateSKUs({
    name,
    totalWorth,
    childPacks,
    discount,
    discountType,
    discountedTotal,
}: GenerateSKUsParameters): PriceLevel[] {
    const productId = addSkuNamePrefix(name);
    const discountedPriceLevel = createFirstDiscountedPriceLevel(
        productId,
        totalWorth,
        discountedTotal,
    );
    const priceLevels: PriceLevel[] = [
        createStrikethroughPriceLevel(productId, totalWorth),
        discountedPriceLevel,
    ];

    let previousPriceLevel = discountedPriceLevel;
    const highestWorth = getHighestWorth(childPacks);

    while (
        previousPriceLevel.price &&
        previousPriceLevel.price > highestWorth
    ) {
        const previousPrice = previousPriceLevel.price ?? 0;
        const step = getPriceNextStep(previousPrice);
        const price: number = previousPrice - step;

        if (price < highestWorth) {
            break;
        }
        const newPriceLevel = createDiscountedPriceLevel(productId, price);

        if (discountType == DiscountTypes.percentage) {
            newPriceLevel.min = Math.floor(price / (1 - discount / 100));
        } else {
            newPriceLevel.min = previousPriceLevel.min - step;
        }
        newPriceLevel.max = previousPriceLevel.min - 1;

        if (newPriceLevel.min < previousPriceLevel.min) {
            priceLevels.push(newPriceLevel);
        }
        previousPriceLevel = newPriceLevel;
    }

    return priceLevels;
}

export function getHighestWorth(childPacks: Pick<Pack, 'worth'>[]): number {
    const max = _.maxBy(childPacks, 'worth');
    return max ? max.worth : 0;
}

export function getPriceNextStep(price: number): number {
    if (price > 100) {
        return 10;
    } else if (price > 40) {
        return 5;
    } else {
        return 1;
    }
}

export function trimDecimalsTo(value: number, decimals: number): number {
    return Number(value.toFixed(decimals));
}

export function isValidFlatDiscount(
    value: number,
    totalWorth: number,
): boolean {
    return value >= 0 && value <= totalWorth;
}

export function isValidPercentageDiscount(value: number): boolean {
    return value >= 0 && value <= 100;
}
